import React, {
    useContext, useCallback, useState, useEffect
} from 'react';
import _ from 'lodash';
import { TranslatorContext } from '@jutro/locale';
import { DropdownSelectField, InlineLoader } from '@jutro/components';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import { WizardPage, wizardProps } from 'gw-portals-wizard-react';
import { ViewModelServiceContext, ViewModelForm } from 'gw-portals-viewmodel-react';
import { ClausesUtil } from 'gw-policycommon-util-js';
import { useDependencies } from 'gw-portals-dependency-react';
import { withAuthenticationContext } from 'gw-digital-auth-react';
import { useValidation } from 'gw-portals-validation-react';
import { messages as paymentMessages } from 'gw-capability-policychange-common-react';
import Schedules from './../../components/Schedules/Schedules';
import metadata from './CoveragesPage.metadata.json5';
import messages from './CoveragesPage.messages';
import styles from './CoveragesPage.module.scss';


const structureCustomQuote = (submissionVM, clauses) => {
    // convert OfferingDTO to CustomQuotedDTO structure
    return {
        coverages: clauses.homeowners
    };
};

const getCustomQuote = (vm, lobPath, lobName, filterChangedClauses = false) => {
    const lobOffering = _.get(vm, `${lobPath}.value`);
    let clausesToUpdate = {
        [lobName]: lobOffering.coverages
    };

    if (filterChangedClauses) {
        clausesToUpdate = ClausesUtil.structureClausesForServer(
            lobOffering.coverages,
            lobName,
            null
        );
    }

    return structureCustomQuote(vm, clausesToUpdate);
};

function CoveragesPage(props) {
    const [loadingClause, updateLoadingClause] = useState();
    const [isLoader, setLoader] = useState(false);
    const translator = useContext(TranslatorContext);
    const { EndorsementService } = useDependencies('EndorsementService');
    const { wizardData, updateWizardData, authHeader, id } = props;
    const { submissionVM } = wizardData;
    const {
        initialValidation,
        isComponentValid,
        disregardFieldValidation,
        registerComponentValidation,
        onValidate
    } = useValidation(id);
    const viewModelService = useContext(ViewModelServiceContext);
    const [extensions, setExtensions] = useState([]);
    const [selectedExtensions, setSelectedExtensions] = useState([]);
    const [searchExtension, setSearchExtension] = useState("");
    const [extensionUpdateIndex, setExtensionUpdate] = useState(-1);
    const [extensionLoading, setExtensionLoading] = useState(false);

    const changeSubmission = useCallback(
        (value, changedPath, index) => {
            _.set(submissionVM, changedPath, value);
            ClausesUtil.setClauseValue(submissionVM, value, changedPath);
            updateWizardData(wizardData);
        },
        [submissionVM, wizardData, updateWizardData]
    );

    const writeValue = useCallback(
        (value, path) => {
            _.set(submissionVM, path, value);
            updateWizardData(wizardData);
        },
        [submissionVM, updateWizardData, wizardData]
    );

    const onUpdateCustomQuote = useCallback(
        (_basePath, lobPath, index) => {
            setLoader(true);
            const lobName = ClausesUtil.getLobNameFromPath(lobPath);
            const customQuote = getCustomQuote(submissionVM, lobPath, lobName, false);
            return EndorsementService.updateCoverages(
                submissionVM.value.jobID,
                [],
                { homeowners: customQuote.coverages },
                authHeader
            ).then((updatedSubmission) => {
                const changedPath = lobPath.replace(/.children/, '');
                const updatedClauses = _.get(updatedSubmission, `${changedPath}.coverages`);
                const newSubmissionVM = viewModelService.clone(submissionVM);
                _.set(submissionVM, `${lobPath}.coverages`, updatedClauses);
                if (submissionVM.transactionCost.amount && submissionVM.transactionCost.amount.value < 0) {
                    _.set(submissionVM, 'transactionCost.amount.value', 0);
                }
                const isLiabilityLinePresent = _.get(newSubmissionVM, `${lobPath}.coverages.isLiabilityLinePresent.value`);
                const removedFieldsFromLiabilityCoverages = isLiabilityLinePresent ? ClausesUtil.getRemovedClausesID(
                    submissionVM,
                    newSubmissionVM,
                    `${lobPath}.coverages.liablityLineCoverages`
                ): [];
                let removedFieldsFromAdditionalCoverages = [];
                if(index){
                    removedFieldsFromAdditionalCoverages = ClausesUtil.getRemovedClausesID(
                        submissionVM,
                        newSubmissionVM,
                        `${lobPath}.coverages.dwellingCoverages.children[${index}].additionalCoverages`
                    );
                }
                const allRemovedFields = [
                    ...removedFieldsFromAdditionalCoverages,
                    ...removedFieldsFromLiabilityCoverages
                ];                
                
                disregardFieldValidation(allRemovedFields);
                updateWizardData(wizardData);
                updateLoadingClause(undefined);
                setLoader(false);
            });
        },
        [
            authHeader,
            disregardFieldValidation,
            EndorsementService,
            submissionVM,
            updateWizardData,
            viewModelService,
            wizardData,
            setLoader
        ]
    );

    const onClauseChange = useCallback(
        (index, schedule, path) => {
            const lobOfferingPath = 'lobData.homeowners.offerings.children[0]';
            writeValue(schedule, path);
            return onUpdateCustomQuote({}, lobOfferingPath);
        },
        [onUpdateCustomQuote, writeValue]
    );

    const syncClauses = useCallback(
        async (value, changedPath, index) => {
            const basePath = ClausesUtil.getObjectPathFromChangedPath(changedPath);
            const baseObject = _.get(submissionVM, basePath);
            updateLoadingClause(baseObject.coveragePublicID || baseObject.publicID);
            await onClauseChange(index);
            updateWizardData(wizardData);
        },
        [onClauseChange, submissionVM, updateWizardData, wizardData]
    );

    const changeSubmissionAndSync = useCallback(
        (value, changedPath, index) => {
            changeSubmission(value, changedPath,index);
            syncClauses(value, changedPath, index);
        },
        [changeSubmission, syncClauses]
    );

    const onNext = useCallback(async () => {
        const updateResponse = await EndorsementService.updatePolicyChangeCoverages(
            submissionVM.value.jobID,
            [],
            { homeowners: submissionVM.value.lobData.homeowners.offerings[0].coverages },
            submissionVM.baseData.autoMultiLineDiscount.value,
            authHeader
        );
        submissionVM.value = await EndorsementService.quoteEndorsement(
            [updateResponse.jobID],
            authHeader
        );
        return wizardData;
    }, [EndorsementService, authHeader, submissionVM.value, wizardData]);

    const onScheduleChange = useCallback(
        async (schedule, path, index) => {
            const changedPath = `lobData.homeowners.offerings.children[0].coverages`
            changeSubmission(schedule, path, index);
            await onClauseChange(index, schedule, path);
            updateWizardData(wizardData);
        },
        [changeSubmission, syncClauses]
    );

    const updateExtensionOnBlur = (index) => {
        setExtensionUpdate(index);
        EndorsementService.updateCoverages(
            submissionVM.value.jobID,
                [],
                { homeowners: _.get(submissionVM, 'lobData.homeowners.offerings.children.0.coverages.value') },
                authHeader
        ).then((response) => {
            setExtensionUpdate(-1);
            submissionVM.value = response;
            updateWizardData(wizardData);
        });
    }


    const updateExtensionValue = (value, path, index) => {        
        const _path = `lobData.homeowners.offerings.children.0.coverages.liabilityExtensions.children.${index}.extensionValue`;
        writeValue(value, _path);        
    }

    const generateExtensionOverrides = () => {
        const overrides = submissionVM.lobData.homeowners.offerings.children[0].coverages.liabilityExtensions.children.map((item, index) => {
            return {
                [`extensionLiabilityTitle${index}`]: {
                    content: `${item['name'].value}`,
                },
                [`extenionName${index}`]: {
                    "label": `${item['description'].value}`,
                    "value": translator({id: item['descriptionValue'].value.name})
                },      
                [`extensionValue${index}`]: {
                    "label": `${item['valueDescription'].value}`,
                    onValueChange: (value, path) => { updateExtensionValue(value, path, index) },
                    onBlur: () => { updateExtensionOnBlur(index) },
                    required: item.required.value,
                    showRequired: true,
                    visible: !_.isUndefined(item['valueDescription'].value)
                },
                [`addExtensionLocation${index}`]:{
                    onClick: () => {addExtensionLocation(item, index) }
                },
                [`extensionLocationContainer${index}`]:{
                    visible: item.accountLocationPublicId.value.length > 0,
                    data: item.accountLocationPublicId.value
                },
                [`deleteLocation${index}`]:{
                    onClick: (value, deleteIndex) => { deleteExtensionLocation(deleteIndex, item, index) } 
                },
                [`extensionLocation${index}`]:{
                    onCell: (item, _index, props) => { return getDataTableCell(item, _index, props, index)}
                },
                [`deleteExtensionButton${index}`]:{
                    onClick: () => { deleteExtension(index) }
                },
                [`extensionLoader${index}`]:{
                    loaded: !(extensionUpdateIndex == index)
                },
                [`extensionLoadedContent${index}`]:{
                    visible: !(extensionUpdateIndex == index)
                }
            };
        });
        return Object.assign({}, ...overrides);
    }

    const generateOverrides = () => {
        const overrides = submissionVM.lobData.homeowners.offerings.children[0].coverages.dwellingCoverages.children.map((item, index) => {
            return {
                [`packageAccordionCard${index}`]: {
                    header: `${translator(messages.packageSpecificCoverages)} - ${translator({ id: item['dwelingName'].value })}`,
                    className: isLoader ? 'disable' : ''
                } ,
                [`hoPolicyChangeAdditionalCoveragesId${index}`]: {
                    loadingClause: loadingClause,
                    onSyncCoverages: (value, changedPath) => { syncClauses(value, changedPath, index)},
                    onChangeSubmissionAndSync: (value, changedPath) => { changeSubmissionAndSync(value, changedPath, index)},
                    onChangeClause: (value, changedPath) => { changeSubmission(value, changedPath, index) },
                    onValidate: onValidate                    
                }, 
                [`scheduleItemContainerId${index}`]:{
                    coverage: item,
                    onScheduleChange: onScheduleChange,
                    covIndex: index,
                    onValidate: onValidate
                }        
            };
        });
        return Object.assign({}, ...overrides);
    }

    const onSelectionChange = (selectedIds) => {
        const _selectedExtensions = _.filter(extensions, (e) => { return selectedIds.indexOf(e.id) != -1 })
        setSelectedExtensions(_selectedExtensions);
        console.log(_selectedExtensions);
    }

    const addExtensions = () => {
        setExtensionLoading(true);
        EndorsementService.addLiabilityExtenstionForProduct(
            [submissionVM.value, selectedExtensions],
            authHeader
        ).then((response) => {
            setSearchExtension("");
            setExtensionLoading(false);
            submissionVM.value = response;
            updateWizardData(wizardData);
        }); 
    };

    const deleteExtension = (index) => {
        setExtensionUpdate(index);
        const deleteExtension = _.get(submissionVM, `lobData.homeowners.offerings.children[0].coverages.liabilityExtensions.value.${index}`);
        EndorsementService.removeLiabilityExtensionForProduct(
            [submissionVM.value, deleteExtension],
            authHeader
        ).then((response) => {
            setExtensionUpdate(-1);
            submissionVM.value = response;
            if(!submissionVM.lobData.homeowners.offerings.children[0].coverages.liabilityExtensions.value){
                submissionVM.lobData.homeowners.offerings.children[0].coverages.liabilityExtensions.value = [];
            }            
            updateWizardData(wizardData);
        });
    }

    const addExtensionLocation = (item, index) => {
        item.accountLocationPublicId.value.push("");
        updateWizardData(wizardData);
    }

    const deleteExtensionLocation = (deleteIndex, item, index) => {
        item.accountLocationPublicId.value.splice(deleteIndex, 1);
        updateWizardData(wizardData);
    }

    const updateExtensionLocation = (value, index, locationIndex) => {
        submissionVM.lobData.homeowners.offerings.children[0].coverages.liabilityExtensions.children[locationIndex].accountLocationPublicId.children[index].value = value;
        updateWizardData(wizardData);
    }

    const handleSearchExtension = (value) => {
        setSearchExtension(value);
    }

    const getDataTableCell = (item, index, props, locationIndex) => {
        const path = props.path;
        const value = item;
        const availableLocations = submissionVM.baseData.policyLocations.value.map( (m) => { m['id'] = m.publicID; return m } );
        return (
            <DropdownSelectField
                className={styles.locationDropdown}
                id={index + path}
                onValueChange={(value, path) => { updateExtensionLocation(value, index, locationIndex) }}
                model={item}
                value={value}
                availableValues={availableLocations}
            />
        )  

    };

    const renderAddExtensionButton = () => {
        return (
            <span>
                {translator(messages.addExtension)}
                {extensionLoading && <InlineLoader loading={extensionLoading} className="gw-inline-loader" />}
            </span>
        )
        
    }

    const changeAutoMultilineDiscount = (value) => {
        _.set(submissionVM.value, 'baseData.autoMultiLineDiscount', value);
        updateWizardData(wizardData);
    }

    const overrideProps = {
        '@field': {
            labelPosition: 'left',
            showRequired: true
        },
        hoPolicyChangeLiabilityCoveragesId: {
            loadingClause: loadingClause,
            onValidate: onValidate,
            // categoryDisplayName: convertCurrencyFormat(
            //     _.get(submissionVM.value, 'lobData.homeowners.offerings[0].coverages.basePremium')
            // )
        },
        extensionsDataTable: {
            data: searchExtension.trim() ? extensions.filter((d) => { return d.name.toLowerCase().indexOf(searchExtension.trim().toLowerCase()) != -1   }) : extensions,
            onSelectionChange: onSelectionChange
        },
        extensionsDataTable: {
            data: searchExtension.trim() ? extensions.filter((d) => { return d.name.toLowerCase().indexOf(searchExtension.trim().toLowerCase()) != -1   }) : extensions,
            onSelectionChange: onSelectionChange
        },
        extensionCoveragesContainer:{
            visible: submissionVM.lobData.homeowners.offerings.children[0].coverages.isLiabilityLinePresent.value
        },
        liabilityExtensionsContainer:{
            visible: submissionVM.lobData.homeowners.offerings.children[0].coverages.isLiabilityLinePresent.value
        },
        extensionHeader:{
            visible: submissionVM.lobData.homeowners.offerings.children[0].coverages.liabilityExtensions
            ? submissionVM.lobData.homeowners.offerings.children[0].coverages.liabilityExtensions.children.length > 0
            : false
        },
        liabilityCoveragesContainer:{
            visible: submissionVM.lobData.homeowners.offerings.children[0].coverages.isLiabilityLinePresent.value
        },
        addExtension:{
            onClick: addExtensions,
            content: renderAddExtensionButton(),
            disabled: extensionLoading
        },
        searchExtensionInput:{
            value:searchExtension
        },
        liabilityAccordionCard:{
            header: translator(messages.liabiliyCoverages)
        },
        autoMultilineDiscountDropdownSelect: {
            availableValues: submissionVM.baseData.multiLineApplicableDiscounts.value.map((item)=>{
                return {code:item, name:item}
            }), 
            value: submissionVM.baseData.autoMultiLineDiscount.value,
            onValueChange: changeAutoMultilineDiscount
        },
        ...generateOverrides(),
        ...generateExtensionOverrides()
    };

    const resolvers = {
        resolveCallbackMap: {
            onChangeClause: changeSubmission,
            onSyncCoverages: syncClauses,
            onChangeSubmissionAndSync: changeSubmissionAndSync,
            onValidate: onValidate,
            getDataTableCell: getDataTableCell,
            deleteExtensionLocation: deleteExtensionLocation,
            handleSearchExtension: handleSearchExtension
        },
        resolveComponentMap: {
            schedules: Schedules
        }
    };
    const isPageLoading = useCallback(() => {
       return  _.isUndefined(loadingClause), [loadingClause] && submissionVM.aspects.valid && submissionVM.aspects.subtreeValid
    }, [submissionVM]);

    useEffect(() => {
        submissionVM.policyChangeReason_EY.value = "other";
        if (submissionVM.transactionCost.amount && submissionVM.transactionCost.amount.value < 0) {
            _.set(submissionVM, 'transactionCost.amount.value', 0);
        }
        submissionVM.isValidationRequired.value = true;
        if(submissionVM.lobData.homeowners.offerings.children[0].coverages.isLiabilityLinePresent.value){
            EndorsementService.getLiabilityExtensions(
                [submissionVM.jobID.value],
                authHeader
            ).then((liabilityExtensions) => {
                const _extensions = liabilityExtensions.map((c) => {
                    c['id'] = c.publicID;
                    return c
                });
                setExtensions(_extensions);
            });
        }
        registerComponentValidation(isPageLoading);
    }, [isPageLoading, registerComponentValidation, submissionVM]);

    return (
        <WizardPage
            showPrevious
            skipWhen={initialValidation}
            disableNext={!isComponentValid}
            onNext={onNext}
            cancelLabel={translator(appConfig.persona === 'policyholder' ? paymentMessages.cancelAllChanges : paymentMessages.cancel)}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                callbackMap={resolvers.resolveCallbackMap}
                onValidationChange={onValidate}
                classNameMap={styles}
                componentMap={resolvers.resolveComponentMap}
            />
        </WizardPage>
    );
}

CoveragesPage.propTypes = wizardProps

export default withAuthenticationContext(CoveragesPage);
